import React, {
	createContext,
	useContext,
	useDeferredValue,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { ChakraProvider, extendTheme } from '@chakra-ui/react';
import { useAuthenticatedUserInfo } from './AuthenticatedUserInfoContext';
import { useCompanySettings } from '../../hooks/useCompany';
import {
	isLightColor,
	paletteFromHex,
	rgbaFromHex,
} from '../../utils/brandPalette';

/** Chakra's default teal, used for the app's primary actions. */
export const DEFAULT_PRIMARY_COLOR = '#319795';

const BrandThemeContext = createContext({
	primaryColor: null,
	setPreviewColor: () => {},
	setPublicColor: () => {},
});

const solidOnLight = (props) =>
	props.colorScheme === 'teal' ? { color: 'gray.900' } : {};

/**
 * The base theme with the `teal` scale (the app's primary colour) rebuilt from the
 * company's brand colour, so buttons, links, checkboxes, tabs, badges and focus rings follow it.
 */
const brandTheme = (baseTheme, primaryColor) => {
	const palette = paletteFromHex(primaryColor);
	if (!palette) return baseTheme;
	const light = isLightColor(primaryColor);
	return extendTheme(
		{
			colors: { teal: palette },
			shadows: { outline: `0 0 0 3px ${rgbaFromHex(primaryColor, 0.45)}` },
			components: light
				? {
						Button: { variants: { solid: solidOnLight } },
						Badge: { variants: { solid: solidOnLight } },
						Tag: { variants: { solid: (p) => ({ container: solidOnLight(p) }) } },
						Checkbox: {
							baseStyle: (p) =>
								p.colorScheme === 'teal'
									? { control: { _checked: { color: 'gray.900' } } }
									: {},
						},
					}
				: {},
		},
		baseTheme,
	);
};

/**
 * Wraps ChakraProvider. The primary colour comes from, in order: a live preview on the
 * brand settings page, the signed-in company's branding, or the portal's public branding.
 */
export const BrandThemeProvider = ({ theme, children }) => {
	const { companyId, token } = useAuthenticatedUserInfo();
	const { data: settings } = useCompanySettings(token ? companyId : null);
	const [previewColor, setPreviewColor] = useState(null);
	const [publicColor, setPublicColor] = useState(null);

	const companyColor =
		token && companyId
			? settings?.data?.settings?.branding?.primaryColor || null
			: null;
	const primaryColor = useDeferredValue(
		previewColor !== null
			? previewColor || null
			: companyColor || publicColor || null,
	);

	const resolvedTheme = useMemo(
		() => (primaryColor ? brandTheme(theme, primaryColor) : theme),
		[theme, primaryColor],
	);
	const value = useMemo(
		() => ({ primaryColor, setPreviewColor, setPublicColor }),
		[primaryColor],
	);

	return (
		<BrandThemeContext.Provider value={value}>
			<ChakraProvider theme={resolvedTheme}>{children}</ChakraProvider>
		</BrandThemeContext.Provider>
	);
};

export const useBrandTheme = () => useContext(BrandThemeContext);

/** Applies a public (pre-login) brand colour while the calling page is mounted. */
export const usePublicBrandColor = (color) => {
	const { setPublicColor } = useBrandTheme();
	useEffect(() => {
		setPublicColor(color || null);
		return () => setPublicColor(null);
	}, [color, setPublicColor]);
};

/**
 * Previews an unsaved brand colour across the app while the calling page is mounted;
 * '' previews the default theme, null stops previewing.
 */
export const useBrandColorPreview = (color) => {
	const { setPreviewColor } = useBrandTheme();
	useEffect(() => {
		setPreviewColor(color ?? null);
	}, [color, setPreviewColor]);
	useEffect(() => () => setPreviewColor(null), [setPreviewColor]);
};
